import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { FaCheck, FaLock } from 'react-icons/fa';
import Loader from '../../Loader';
import { ROUTES } from '../../../assets/constants/strings';
import { getAdminPlans, updatePlan } from '../../../services/axios';

const mapStateToProps = (state) => ({
  token: state.user.user ? state.user.user.token : null,
});

const connector = connect(mapStateToProps);

// True when the row's editable fields differ from what the server last returned — used to gate Save
// and surface an "unsaved" marker so the admin knows an edit is pending.
const isDirty = (plan, original) =>
  !original ||
  Number(plan.price) !== Number(original.price) ||
  (parseInt(plan.queryLimit, 10) || 0) !== (parseInt(original.queryLimit, 10) || 0) ||
  !!plan.allowsApiKeys !== !!original.allowsApiKeys;

// Trial cards come first: that is the order a customer meets the two tiers in.
const ROLE_ORDER = { Trial: 0, Enterprise: 1 };
const byRole = (a, b) =>
  (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9) || (a.name || '').localeCompare(b.name || '');

const num = (n) => Number(n || 0).toLocaleString();
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// A read-only reader cap, phrased as its effect rather than as a number and a field name.
const capText = (value, noun) =>
  Number(value) > 0
    ? `${num(value)} ${noun}`
    : `${noun.charAt(0).toUpperCase()}${noun.slice(1)} uncapped`;

// What the customer is actually shown for this plan. Both customer-facing Plans pages hardcode
// these two strings — no price from this catalogue reaches them.
const customerSees = (plan) => {
  if (plan.role === 'Enterprise') {
    return (
      <>
        Customers see <b>Contact us · tailored</b>. You provision it per customer.
      </>
    );
  }
  if (plan.trialDays) {
    return (
      <>
        Customers see <b>Free</b>, and the trial runs for <b>{plural(plan.trialDays, 'day', 'days')}</b>.
      </>
    );
  }
  return (
    <>
      Customers see <b>Free</b> on the Plans page.
    </>
  );
};

// Admin: what each plan in the catalogue includes. Names and product lines are fixed; the limits,
// the API-key grant and the (internal) price are editable and take effect immediately. Plans no
// signup or provisioning path can reach are listed separately as retired, and are read-only.
const Plans = ({ token }) => {
  const [plans, setPlans] = useState(null);
  const [originals, setOriginals] = useState({}); // { [id]: plan } — last-saved snapshot for dirty checks
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [msg, setMsg] = useState(null); // { id, text, ok }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      setLoading(true);
      const res = await getAdminPlans(token);
      if (cancelled) return;
      const list = res || [];
      setPlans(list);
      setOriginals(Object.fromEntries(list.map((p) => [p.id, { ...p }])));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const setField = (id, field, value) => {
    setPlans((ps) => ps.map((p) => (p.id === id ? { ...p, [field]: value } : p)));
    // Editing clears any stale save message for that row.
    setMsg((m) => (m && m.id === id ? null : m));
  };

  const save = async (plan) => {
    setSavingId(plan.id);
    setMsg(null);
    const res = await updatePlan(token, {
      id: plan.id,
      price: Number(plan.price) || 0,
      queryLimit: parseInt(plan.queryLimit, 10) || 0,
      allowsApiKeys: !!plan.allowsApiKeys,
    });
    setSavingId(null);
    if (res && res.success && res.plan) {
      setPlans((ps) => ps.map((p) => (p.id === plan.id ? res.plan : p)));
      setOriginals((o) => ({ ...o, [plan.id]: { ...res.plan } }));
      setMsg({ id: plan.id, text: 'Saved', ok: true });
    } else {
      setMsg({ id: plan.id, text: (res && res.reason) || 'Could not save.', ok: false });
    }
  };

  if (loading) {
    return (
      <div className='adm-panel'>
        <Loader />
      </div>
    );
  }
  if (!plans) {
    return <div className='adm-empty'>Couldn't load plans.</div>;
  }

  // Who is on this plan. The current ones link to the customer list filtered down to exactly them;
  // the lapsed ones can't, because that list only carries subscriptions that still grant access — so
  // they are stated rather than linked, which at least stops an empty plan reading as an unused one.
  const Who = ({ plan }) => {
    const lapsed = plan.lapsedCustomerCount || 0;
    const lapsedNote = lapsed > 0 && (
      <span
        className='adm-plancard__lapsed'
        title='Were on this plan but no longer have access through it — a lapsed trial, or a cancelled or expired subscription.'
      >
        {lapsed} lapsed
      </span>
    );

    if (plan.customerCount > 0) {
      return (
        <span className='adm-plancard__whorow'>
          <Link
            className='adm-plancard__who'
            to={`${ROUTES.APIAdmin}/customers?plan=${encodeURIComponent(plan.name)}`}
          >
            {plural(plan.customerCount, 'customer', 'customers')}
          </Link>
          {lapsedNote}
        </span>
      );
    }
    return (
      <span className='adm-plancard__whorow'>
        <span className='adm-plancard__who adm-plancard__who--none'>
          {lapsed > 0 ? 'None active' : 'No customers yet'}
        </span>
        {lapsedNote}
      </span>
    );
  };

  const Field = ({ label, hint, children }) => (
    <div className='adm-planfield'>
      <span className='adm-planfield__label'>{label}</span>
      <div className='adm-planfield__row'>{children}</div>
      {hint && <p className='adm-planfield__hint'>{hint}</p>}
    </div>
  );

  const Card = ({ plan }) => {
    const dirty = isDirty(plan, originals[plan.id]);
    const isApi = plan.productLine === 'DeveloperApi';
    const isEnterprise = plan.role === 'Enterprise';
    const limit = parseInt(plan.queryLimit, 10) || 0;
    const role = isEnterprise ? 'ent' : 'trial';

    return (
      <article className={`adm-plancard adm-plancard--${role}`}>
        <div className='adm-plancard__top'>
          <span className={`adm-badge adm-badge--role adm-badge--role-${role}`}>{plan.role}</span>
          <Who plan={plan} />
        </div>
        <h3 className='adm-plancard__name'>{plan.name}</h3>
        <p className='adm-plancard__see'>{customerSees(plan)}</p>

        <div className='adm-planfields'>
          <Field
            label={isApi ? 'Included calls' : 'Queries per cycle'}
            hint={
              isApi
                ? isEnterprise
                  ? 'Set a per-customer cap on their account instead, when a deal needs one.'
                  : 'A one-off allowance for the life of the account, not a monthly reset.'
                : isEnterprise
                ? 'Seats and price are set per customer, on their account.'
                : null
            }
          >
            <input
              className='adm-input adm-input--num'
              type='number'
              min='0'
              value={plan.queryLimit}
              onChange={(e) => setField(plan.id, 'queryLimit', e.target.value)}
              aria-label={isApi ? 'Included calls' : 'Queries per cycle'}
            />
            {limit === 0 ? (
              <span className='adm-badge adm-badge--role adm-badge--role-ent'>Uncapped</span>
            ) : (
              <span className='adm-planfield__unit'>
                {isApi ? 'calls in total' : 'then blocked until the cycle turns over'}
              </span>
            )}
          </Field>

          {isApi ? (
            <Field
              label='API keys'
              hint={
                isEnterprise
                  ? 'Required — this is the plan paying integrations run on.'
                  : 'Trial customers can create a key, so they can test a real integration.'
              }
            >
              <label className='adm-switch'>
                <input
                  type='checkbox'
                  checked={!!plan.allowsApiKeys}
                  onChange={(e) => setField(plan.id, 'allowsApiKeys', e.target.checked)}
                />
                <span className='adm-switch__track'>
                  <span className='adm-switch__thumb' />
                </span>
                <span className='adm-switch__label'>{plan.allowsApiKeys ? 'Allowed' : 'Off'}</span>
              </label>
            </Field>
          ) : (
            <Field
              label='Other caps on this plan'
              hint='Enforced against the plan, but not editable here — shown so the plan’s real shape is visible.'
            >
              <span className='adm-caps'>
                <span className='adm-caps__item'>{capText(plan.summaryLimit, 'summaries')}</span>
                <span className='adm-caps__item'>{capText(plan.pageViewLimit, 'page views')}</span>
                <span className='adm-caps__item'>{capText(plan.shareLimit, 'shares')}</span>
              </span>
            </Field>
          )}

          <Field
            label='Internal price'
            hint={
              isApi && isEnterprise
                ? 'Not what anyone pays — it is how provisioning recognises the paid plan. Saving $0 is refused.'
                : 'Nobody is billed from this screen.'
            }
          >
            <span className='adm-inputgroup'>
              <span className='adm-inputgroup__addon'>$</span>
              <input
                className='adm-input adm-input--joined'
                type='number'
                min='0'
                step='0.01'
                value={plan.price}
                onChange={(e) => setField(plan.id, 'price', e.target.value)}
                aria-label='Internal price'
              />
            </span>
            {isApi && isEnterprise ? (
              <span className='adm-planfield__lock'>
                <FaLock /> Must stay above $0
              </span>
            ) : (
              <span className='adm-planfield__unit'>
                {isEnterprise ? 'reference only — the real price is per customer' : 'reference only'}
              </span>
            )}
          </Field>
        </div>

        <div className='adm-plancard__foot'>
          <span className='adm-saverow'>
            <button
              className='adm-btn adm-btn--teal adm-btn--sm'
              type='button'
              onClick={() => save(plan)}
              disabled={savingId === plan.id || !dirty}
            >
              {savingId === plan.id ? 'Saving…' : 'Save'}
            </button>
            {dirty && savingId !== plan.id && (
              <span className='adm-saverow__pending' title='Unsaved changes'>
                Unsaved
              </span>
            )}
            {!dirty && msg && msg.id === plan.id && msg.ok && (
              <span className='adm-saverow__ok'>
                <FaCheck /> {msg.text}
              </span>
            )}
            {msg && msg.id === plan.id && !msg.ok && (
              <span className='adm-saverow__err'>{msg.text}</span>
            )}
          </span>
        </div>
      </article>
    );
  };

  const Group = ({ title, dot, rows }) => {
    const customers = rows.reduce((sum, p) => sum + (p.customerCount || 0), 0);
    return (
      <div className='adm-panel adm-panel--flush'>
        <div className='adm-panel__head'>
          <span className='adm-inline'>
            <span className={`adm-dot adm-dot--${dot}`} />
            <b>{title}</b>
          </span>
          <span className='adm-panel__headnote'>
            {plural(customers, 'customer', 'customers')} across {plural(rows.length, 'plan', 'plans')}
          </span>
        </div>
        <div className='adm-plancards'>
          {rows.map((p) => (
            <Card key={p.id} plan={p} />
          ))}
        </div>
      </div>
    );
  };

  const live = (productLine) =>
    plans.filter((p) => p.productLine === productLine && p.role !== 'Retired').sort(byRole);
  // Retired tiers read as the price ladder they were, so keep them in that order.
  const retired = plans
    .filter((p) => p.role === 'Retired')
    .sort((a, b) => Number(a.price) - Number(b.price));
  const retiredCustomers = retired.reduce((sum, p) => sum + (p.customerCount || 0), 0);

  return (
    <section className='adm-section'>
      <p className='adm-sub'>
        What each plan includes. Trials are self-serve and capped; Enterprise is provisioned per
        customer, and that customer’s negotiated price and any custom limit live on <b>their</b>{' '}
        account rather than here. Prices below are internal reference only — no payment provider is
        connected. Changes apply immediately to everyone on the plan.
      </p>

      <div className='adm-plangroups'>
        <Group title='Developer API' dot='api' rows={live('DeveloperApi')} />
        <Group title='RegulationCity App' dot='reader' rows={live('ReaderApp')} />

        {retired.length > 0 && (
          <div className='adm-panel adm-panel--flush adm-panel--retired'>
            <div className='adm-panel__head'>
              <span className='adm-inline'>
                <span className='adm-dot adm-dot--off' />
                <b>Retired tiers — not offered to anyone</b>
              </span>
              <span className='adm-panel__headnote'>
                {plural(retiredCustomers, 'customer', 'customers')}
              </span>
            </div>
            <div className='adm-tablewrap'>
              <table className='adm-table'>
                <thead>
                  <tr>
                    <th>Plan</th>
                    <th>Was</th>
                    <th>Caps (queries · summaries · page views · shares)</th>
                    <th>Customers</th>
                  </tr>
                </thead>
                <tbody>
                  {retired.map((p) => (
                    <tr key={p.id}>
                      <td className='adm-table__nowrap'>
                        <span className='adm-status adm-status--archived'>Retired</span>{' '}
                        <b>{p.name}</b>
                      </td>
                      <td className='adm-table__nowrap'>${num(p.price)} /mo</td>
                      <td className='adm-table__muted'>
                        {[p.queryLimit, p.summaryLimit, p.pageViewLimit, p.shareLimit]
                          .map((v) => (Number(v) > 0 ? num(v) : '∞'))
                          .join(' · ')}
                      </td>
                      <td className='adm-table__nowrap'>
                        {p.customerCount > 0 ? (
                          <Link
                            className='adm-link'
                            to={`${ROUTES.APIAdmin}/customers?plan=${encodeURIComponent(p.name)}`}
                          >
                            {p.customerCount}
                          </Link>
                        ) : (
                          <span className='adm-table__muted'>0</span>
                        )}
                        {p.lapsedCustomerCount > 0 && (
                          <span className='adm-plancard__lapsed'>{p.lapsedCustomerCount} lapsed</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className='adm-orgnote'>
              Left over from the self-serve price ladder, which was dropped. No signup or
              provisioning path selects these, so nobody new can reach them — which is also why they
              aren’t editable here. Once anyone still on one is moved to Trial or Enterprise, the
              rows can be deleted.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default connector(Plans);
